import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { LayoutGrid, Layers, Globe2, BarChart3, ShieldCheck } from 'lucide-react';
interface SlideControlTowerProps {
  onContinue: () => void;
}
const NAV = [{
  label: 'Overview',
  icon: LayoutGrid,
  active: true
}, {
  label: 'Campaigns',
  icon: Layers,
  active: false
}, {
  label: 'Markets',
  icon: Globe2,
  active: false
}, {
  label: 'Insights',
  icon: BarChart3,
  active: false
}, {
  label: 'Governance',
  icon: ShieldCheck,
  active: false
}] as const;
const KPIS = [{
  value: '34',
  label: 'Live Snaks'
}, {
  value: '27',
  label: 'Markets active'
}, {
  value: '12',
  label: 'Brands'
}, {
  value: '486',
  label: 'Localized assets'
}] as const;
const ROWS = [{
  brand: 'Enstilar®',
  campaign: 'Adherence series — master Snaks',
  ta: 'Psoriasis',
  markets: 'DE · UK · PL',
  live: true,
  trend: [14, 18, 17, 24, 28, 34]
}, {
  brand: 'Fucidin®',
  campaign: 'Guideline refresh for GPs',
  ta: 'Skin infections',
  markets: '6 markets',
  live: true,
  trend: [10, 12, 16, 19, 21, 27]
}, {
  brand: 'Protopic®',
  campaign: 'Eczema maintenance teasers',
  ta: 'Atopic dermatitis',
  markets: 'ES · IT',
  live: true,
  trend: [22, 20, 24, 25, 29, 31]
}, {
  brand: 'Anzupgo®',
  campaign: 'Hand eczema application guide',
  ta: 'Chronic hand eczema',
  markets: 'DK · CH pilot',
  live: false,
  trend: [26, 24, 21, 19, 17, 15]
}] as const;
function Sparkline({
  points,
  up
}: {
  points: readonly number[];
  up: boolean;
}) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const path = points.map((p, i) => {
    const x = i / (points.length - 1) * 100;
    const y = 24 - (p - min) / Math.max(1, max - min) * 20;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="w-24 h-6 overflow-visible" aria-hidden="true" data-id="element-443" data-mp="17yhmkr48jr3z">
      <motion.path d={path} fill="none" stroke={up ? '#7c3aed' : '#cbd5e1'} strokeWidth="2.5" strokeLinecap="round" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 1,
      ease: 'easeOut'
    }} data-id="element-444" data-mp="1ctx1znhw5v2i" />
    </svg>;
}
export function SlideControlTower({
  onContinue
}: SlideControlTowerProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [200, 700, 1300, 2200].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-6 py-8 md:px-14 md:py-10 max-w-[1400px] mx-auto" data-id="element-445" data-mp="69xgj507wn0n">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="shrink-0" data-id="element-446" data-mp="2pn68fqrhqv4">
        <div className="flex items-baseline justify-between gap-4" data-id="element-447" data-mp="3p5hp6eh0ahtr">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-xs md:text-sm" data-id="element-448" data-mp="3p5rdlxmekgxc">
            For HQ — the platform
          </p>
          <p className="text-white/30 font-semibold tracking-[0.2em] uppercase text-[10px] md:text-xs" data-id="element-449" data-mp="lpax757ye90t">
            Snakker for LEO Pharma
          </p>
        </div>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-2" data-id="element-450" data-mp="gnlmjqon7xd9">
          Control Tower —{' '}
          <span className="text-teal-400" data-id="element-451" data-mp="cpxmyac09gf1">one view of every campaign</span>
        </h2>
      </motion.div>

      {/* Dashboard mock */}
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.98
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 24,
      scale: stage >= 1 ? 1 : 0.98
    }} transition={{
      duration: 0.6
    }} className="flex-1 min-h-0 mt-6 rounded-2xl border border-navy-600 bg-navy-900 shadow-[0_20px_60px_rgba(15,23,42,0.08)] overflow-hidden flex flex-col" data-id="element-452" data-mp="2xeln88whhxl9">
        {/* Browser chrome */}
        <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-navy-600 bg-navy-700/40" data-id="element-453" data-mp="f8hhrkd0sxus">
          <div className="flex items-center gap-1.5" data-id="element-454" data-mp="12ybum1xcamf1">
            <span className="w-2.5 h-2.5 rounded-full bg-navy-500" data-id="element-455" data-mp="17w51kx6gvkgd" />
            <span className="w-2.5 h-2.5 rounded-full bg-navy-500" data-id="element-456" data-mp="17vbx62uzd71e" />
            <span className="w-2.5 h-2.5 rounded-full bg-navy-500" data-id="element-457" data-mp="tvaqg6s1b5e0" />
          </div>
          <div className="px-3 py-1 rounded-md bg-navy-900 border border-navy-600 text-white/45 text-xs font-medium" data-id="element-458" data-mp="yunmni4spot9">
            controltower.snakker.com / leo-pharma
          </div>
        </div>

        <div className="flex-1 min-h-0 flex" data-id="element-459" data-mp="3jlxtbd0fifg4">
          {/* Sidebar */}
          <div className="hidden md:flex w-48 shrink-0 flex-col gap-1 border-r border-navy-600 p-4" data-id="element-460" data-mp="3b4neih9ruoll">
            <p className="text-teal-400 font-bold text-xs tracking-[0.2em] uppercase mb-3" data-id="element-461" data-mp="37324d3omgof3">
              LEO
            </p>
            {NAV.map(item => {
            const Icon = item.icon;
            return <div key={item.label} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium ${item.active ? 'bg-teal-400/10 text-teal-400' : 'text-white/50'}`} data-id="element-462" data-mp="3g3gt6d0solnc">
                  <Icon className="w-4 h-4" data-id="element-463" data-mp="2ngw27do8ebkb" />
                  {item.label}
                </div>;
          })}
          </div>

          {/* Main panel */}
          <div className="flex-1 min-w-0 p-5 flex flex-col gap-4" data-id="element-464" data-mp="2xc7hpy7grmt4">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-465" data-mp="29nem62ur7gz1">
              <h3 className="text-white font-semibold text-base md:text-lg" data-id="element-466" data-mp="29lbl8ns2c3fj">
                All Snaks — global overview
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-467" data-mp="8m88jtex8l9b">
                {['Brand', 'Region', 'Therapeutic area', 'Status'].map(f => <span key={f} className="px-3 py-1 rounded-full border border-navy-600 text-white/50 text-xs font-medium" data-id="element-468" data-mp="dip0yzt5lls7">
                    {f}
                  </span>)}
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-469" data-mp="3lu1jxmagtzg3">
              {KPIS.map((kpi, idx) => <motion.div key={kpi.label} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              y: stage >= 2 ? 0 : 10
            }} transition={{
              delay: stage >= 2 ? idx * 0.08 : 0
            }} className="rounded-xl border border-navy-600 bg-navy-700/30 px-4 py-3" data-id="element-470" data-mp="3usn1pf845aft" data-mpi={idx}>
                  <p className="text-2xl md:text-3xl font-serif text-white leading-none" data-id="element-471" data-mp="1k62xrsfpjry8">
                    {kpi.value}
                  </p>
                  <p className="text-white/45 text-xs mt-1.5" data-id="element-472" data-mp="1fdta6j22f3z4">{kpi.label}</p>
                </motion.div>)}
            </div>

            {/* Table */}
            <div className="flex-1 min-h-0 overflow-hidden" data-id="element-473" data-mp="16fzhdi3iesgp">
              <div className="grid grid-cols-[1.1fr_1.6fr_1.2fr_0.9fr_auto_auto] gap-3 px-2 pb-2 border-b border-navy-600 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/35" data-id="element-474" data-mp="11fv1nuafe1rj">
                <span data-id="element-475" data-mp="sf6m2v5dvtcj">Brand</span>
                <span data-id="element-476" data-mp="sfykseua2771">Campaign</span>
                <span className="hidden lg:block" data-id="element-477" data-mp="sgzsqafzydkj">Therapeutic area</span>
                <span className="hidden lg:block" data-id="element-478" data-mp="2qtrp33jxck89">Markets</span>
                <span data-id="element-479" data-mp="2mthbb4pw4piu">Live</span>
                <span className="text-right" data-id="element-480" data-mp="2mu98ul31sxbr">30 days</span>
              </div>
              {ROWS.map((row, idx) => <motion.div key={row.brand} initial={{
              opacity: 0,
              x: -12
            }} animate={{
              opacity: stage >= 3 ? 1 : 0,
              x: stage >= 3 ? 0 : -12
            }} transition={{
              delay: stage >= 3 ? idx * 0.12 : 0
            }} className="grid grid-cols-[1.1fr_1.6fr_1.2fr_0.9fr_auto_auto] gap-3 items-center px-2 py-3 border-b border-navy-600/70" data-id="element-481" data-mp="362hepol8bo4s" data-mpi={idx}>
                  <span className="text-white font-semibold text-sm truncate" data-id="element-482" data-mp="2delngzefiofe">
                    {row.brand}
                  </span>
                  <span className="text-white/80 text-sm truncate" data-id="element-483" data-mp="2iehjabcz39jv">
                    {row.campaign}
                  </span>
                  <span className="hidden lg:block text-white/45 text-sm truncate" data-id="element-484" data-mp="gkqjbdgbrpg9">
                    {row.ta}
                  </span>
                  <span className="hidden lg:block text-white/45 text-sm truncate" data-id="element-485" data-mp="qpqi9qv6y0zo">
                    {row.markets}
                  </span>
                  <span className={`w-9 h-5 rounded-full flex items-center px-0.5 ${row.live ? 'bg-teal-400 justify-end' : 'bg-navy-600 justify-start'}`} role="img" aria-label={row.live ? 'Live' : 'Paused'} data-id="element-486" data-mp="zh8w3v4o04pl">
                    <span className="w-4 h-4 rounded-full bg-white shadow-sm" data-id="element-487" data-mp="19hj7da753224" />
                  </span>
                  <span className="flex justify-end" data-id="element-488" data-mp="14ipu9ivrld2h">
                    <Sparkline points={row.trend} up={row.live} data-id="element-489" data-mp="1ietm55j619bs" />
                  </span>
                </motion.div>)}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Footnotes */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 12
    }} className="shrink-0 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-navy-600" data-id="element-490" data-mp="3k7rllk7g705p">
        {['A cross-brand, cross-region view of every Snak and campaign in LEO', 'Live and historical performance across markets and therapeutic areas', 'A control layer for oversight, reuse of best practice and on/off control'].map(note => <div key={note} className="flex items-start gap-3" data-id="element-491" data-mp="qpncnhh85tu4">
            <span className="w-4 h-[2px] bg-teal-400 mt-2.5 shrink-0" data-id="element-492" data-mp="lr3qph2msi1r" />
            <p className="text-white/70 text-sm leading-relaxed" data-id="element-493" data-mp="h0n9dlvowca4">{note}</p>
          </div>)}
      </motion.div>
    </div>;
}